// Any timed event a day column can lay out — minutes are measured from
// midnight, so the algorithm needs no knowledge of the calendar's hour range.
export interface CalendarLayoutEvent {
  id: string;
  startMinutes: number;
  endMinutes: number;
}

export interface LaidOutEvent<T extends CalendarLayoutEvent> {
  event: T;
  // Horizontal placement within the day column, as fractions of its width.
  leftFraction: number;
  widthFraction: number;
}

function compareByStartThenLongest(a: CalendarLayoutEvent, b: CalendarLayoutEvent): number {
  if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
  return b.endMinutes - a.endMinutes;
}

// Group transitively-overlapping events into clusters (events must be sorted).
function groupIntoClusters<T extends CalendarLayoutEvent>(sorted: T[]): T[][] {
  const clusters: T[][] = [];
  let currentCluster: T[] = [];
  let currentClusterEnd = -Infinity;

  for (const ev of sorted) {
    if (currentCluster.length === 0 || ev.startMinutes < currentClusterEnd) {
      currentCluster.push(ev);
      currentClusterEnd = Math.max(currentClusterEnd, ev.endMinutes);
    } else {
      clusters.push(currentCluster);
      currentCluster = [ev];
      currentClusterEnd = ev.endMinutes;
    }
  }
  if (currentCluster.length > 0) clusters.push(currentCluster);

  return clusters;
}

// Greedily pack a cluster's events into the fewest columns.
function packIntoColumns<T extends CalendarLayoutEvent>(cluster: T[]): T[][] {
  const columns: T[][] = [];

  for (const ev of cluster) {
    const fittingColumn = columns.find(
      (column) => column[column.length - 1].endMinutes <= ev.startMinutes
    );
    if (fittingColumn) {
      fittingColumn.push(ev);
    } else {
      columns.push([ev]);
    }
  }

  return columns;
}

// How many columns an event can widen into before hitting a conflict.
function computeColumnSpan<T extends CalendarLayoutEvent>(
  ev: T,
  columnIndex: number,
  columns: T[][]
): number {
  let span = 1;
  for (let lookahead = columnIndex + 1; lookahead < columns.length; lookahead++) {
    const conflicts = columns[lookahead].some(
      (other) => other.startMinutes < ev.endMinutes && other.endMinutes > ev.startMinutes
    );
    if (conflicts) break;
    span++;
  }
  return span;
}

function layoutCluster<T extends CalendarLayoutEvent>(cluster: T[]): LaidOutEvent<T>[] {
  const columns = packIntoColumns(cluster);

  // Keyed by event object, not by id: ids are not guaranteed unique within a
  // day, and an id-keyed map would give both events the same column.
  const columnIndexByEvent = new Map<T, number>();
  columns.forEach((column, columnIndex) => {
    for (const ev of column) columnIndexByEvent.set(ev, columnIndex);
  });

  return cluster.map((ev) => {
    const columnIndex = columnIndexByEvent.get(ev) ?? 0;
    const span = computeColumnSpan(ev, columnIndex, columns);
    return {
      event: ev,
      leftFraction: columnIndex / columns.length,
      widthFraction: span / columns.length
    };
  });
}

// Lays out one day's events side by side so overlapping events never cover
// each other (the classic calendar column algorithm):
//  1. group transitively-overlapping events into clusters,
//  2. greedily pack each cluster's events into the fewest columns,
//  3. let an event widen into adjacent columns until it would hit a conflict.
export function layoutEventsForDay<T extends CalendarLayoutEvent>(events: T[]): LaidOutEvent<T>[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort(compareByStartThenLongest);
  return groupIntoClusters(sorted).flatMap((cluster) => layoutCluster(cluster));
}
